import type { ReactNode } from 'react'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

import { tokens, shortScreen } from '@configuration/theme'

export interface CardProps {
  /** Card heading (rendered as an h3 in the `card-hd` block). */
  title?: ReactNode
  /** Sub-text under the title. */
  description?: ReactNode
  sx?: SxProps<Theme>
  dataTestId?: string
  children?: ReactNode
}

// Bordered content card from the design (`.card`): hairline border, 10px
// radius, 24px padding, optional `card-hd` (title + description).
const Card = ({ title, description, sx, dataTestId = 'card', children }: CardProps) => (
  <Box
    data-testid={dataTestId}
    sx={[
      {
        background: tokens.white,
        border: `1px solid ${tokens.borderDefault}`,
        borderRadius: tokens.radiusLg,
        padding: '24px',
        [shortScreen.tight]: { padding: '18px' },
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {(title || description) && (
      <Box sx={{ mb: '20px' }}>
        {title && (
          <Box component='h3' sx={{ fontSize: 16, fontWeight: 500, m: '0 0 4px' }}>
            {title}
          </Box>
        )}
        {description && (
          <Box component='p' sx={{ fontSize: 13, color: tokens.businessBlack60, m: 0 }}>
            {description}
          </Box>
        )}
      </Box>
    )}
    {children}
  </Box>
)

export default Card
